package com.iptvplayer.shared.data.repository

import com.iptvplayer.shared.data.remote.xtream.XtreamApiClient
import com.iptvplayer.shared.domain.model.EpgNowNext
import com.iptvplayer.shared.domain.model.EpgProgram
import com.iptvplayer.shared.domain.model.IptvSource
import com.iptvplayer.shared.domain.repository.ContentRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Deferred
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.async
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.datetime.Clock

/**
 * Now/next programme info for live channels (Xtream `get_short_epg`).
 *
 * Kept separate from [ContentRepository] on purpose: EPG is a lazy, per-row
 * lookup with its own lifecycle, not part of the catalog sync. Results are
 * cached in memory per channel (~10 min) so scrolling a channel list doesn't
 * refetch, and concurrent lookups for the same channel share one request.
 *
 * Only Xtream sources have EPG here; anything else resolves to `null`.
 */
class EpgService(
    private val contentRepository: ContentRepository,
    private val xtreamApiClient: XtreamApiClient
) {

    private data class CacheEntry(
        val programs: List<EpgProgram>,
        val fetchedAtMs: Long,
        val success: Boolean
    )

    private data class EpgKey(val sourceId: String, val streamId: Int) {
        val cacheKey: String get() = "${sourceId}_live_$streamId"
    }

    private val mutex = Mutex()
    private val cache = mutableMapOf<String, CacheEntry>()
    private val inFlight = mutableMapOf<String, Deferred<List<EpgProgram>>>()
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    /**
     * Now/next for a catalog channel id ("<sourceId>_live_<streamId>").
     * Returns `null` when the channel isn't an Xtream live stream or the
     * panel has no EPG for it — callers should simply render nothing.
     */
    suspend fun nowNext(channelId: String): EpgNowNext? {
        val key = parseChannelId(channelId) ?: return null
        return nowNextFor(key)
    }

    /**
     * Player variant: prefers deriving the stream from [streamUrl] (correct
     * after channel zapping, when the original [channelId] is stale) and
     * falls back to [channelId] (e.g. watch-party relay URLs).
     */
    suspend fun nowNextForPlayback(channelId: String?, streamUrl: String?): EpgNowNext? {
        val key = streamUrl?.let { parseStreamUrl(it) }
            ?: channelId?.let { parseChannelId(it) }
            ?: return null
        return nowNextFor(key)
    }

    // ──────────────────── internals ────────────────────

    private suspend fun nowNextFor(key: EpgKey): EpgNowNext? {
        val programs = programsFor(key)
        if (programs.isEmpty()) return null
        val nowMs = Clock.System.now().toEpochMilliseconds()
        val current = programs.firstOrNull { nowMs >= it.startMs && nowMs < it.endMs }
        val next = programs.filter { it.startMs > nowMs }.minByOrNull { it.startMs }
        if (current == null && next == null) return null
        return EpgNowNext(now = current, next = next)
    }

    private suspend fun programsFor(key: EpgKey): List<EpgProgram> {
        val nowMs = Clock.System.now().toEpochMilliseconds()
        val deferred = mutex.withLock {
            cache[key.cacheKey]?.let { cached ->
                val ttl = if (cached.success) SUCCESS_TTL_MS else FAILURE_TTL_MS
                if (nowMs - cached.fetchedAtMs < ttl) return cached.programs
            }
            inFlight[key.cacheKey] ?: fetchAsync(key).also { inFlight[key.cacheKey] = it }
        }
        return try {
            deferred.await()
        } catch (_: Exception) {
            emptyList()
        }
    }

    private fun fetchAsync(key: EpgKey): Deferred<List<EpgProgram>> = scope.async {
        var success = false
        val programs = try {
            val source = contentRepository.getSources().first()
                .filterIsInstance<IptvSource.XtreamSource>()
                .firstOrNull { it.id == key.sourceId }
            if (source == null) {
                emptyList()
            } else {
                val response = xtreamApiClient.getShortEpg(
                    serverUrl = source.serverUrl,
                    username = source.username,
                    password = source.password,
                    streamId = key.streamId,
                    limit = 2
                )
                success = true
                xtreamApiClient.mapShortEpg(response)
            }
        } catch (_: Exception) {
            emptyList()
        }
        mutex.withLock {
            cache[key.cacheKey] = CacheEntry(
                programs = programs,
                fetchedAtMs = Clock.System.now().toEpochMilliseconds(),
                success = success
            )
            inFlight.remove(key.cacheKey)
        }
        programs
    }

    /** "<sourceId>_live_<streamId>" → key; anything else (M3U etc.) → null. */
    private fun parseChannelId(channelId: String): EpgKey? {
        val idx = channelId.lastIndexOf(LIVE_MARKER)
        if (idx <= 0) return null
        val streamId = channelId.substring(idx + LIVE_MARKER.length).toIntOrNull() ?: return null
        return EpgKey(sourceId = channelId.substring(0, idx), streamId = streamId)
    }

    /**
     * "…/live/<user>/<pass>/<streamId>.ts" → key, matched to a known Xtream
     * source by URL prefix. Lets the player resolve EPG after zapping.
     */
    private suspend fun parseStreamUrl(url: String): EpgKey? {
        val match = LIVE_URL_REGEX.find(url) ?: return null
        val streamId = match.groupValues[1].toIntOrNull() ?: return null
        val source = contentRepository.getSources().first()
            .filterIsInstance<IptvSource.XtreamSource>()
            .firstOrNull { url.startsWith(it.serverUrl.trimEnd('/')) }
            ?: return null
        return EpgKey(sourceId = source.id, streamId = streamId)
    }

    private companion object {
        const val SUCCESS_TTL_MS = 10 * 60 * 1000L
        const val FAILURE_TTL_MS = 2 * 60 * 1000L
        const val LIVE_MARKER = "_live_"
        val LIVE_URL_REGEX = Regex("""/live/[^/]+/[^/]+/(\d+)(?:\.[A-Za-z0-9]+)?$""")
    }
}
